import { AlertCircle, Bot, CalendarClock, Clock, StickyNote } from "lucide-react";
import * as React from "react";
import { Badge } from "@/components/ui/badge";
import { Avatar, Tooltip } from "@/components/ui/misc";
import { useAuth } from "@/lib/auth";
import {
  CHANNEL_LABELS, MESSAGE_STATUS_LABELS, messageDetail, type Activity, type CommentActivity, type EventActivity,
  type MessageActivity,
} from "@/lib/conversations";
import { callbackStatus, callbackWhen, knownZone } from "@/lib/callbacks";
import { noteTitle } from "@/lib/contacts";
import { SCHEDULED_WHY, automatedBy, scheduledFor, seesAutomation, workflowSender } from "@/lib/texts";
import type { LeadCard } from "@/lib/types";
import { cn, formatDateTime } from "@/lib/utils";
import { CallActivityCard } from "@/features/conversations/call-activity";
import { CHANNEL_ICONS, CHANNEL_TONES, typeIcon } from "@/features/conversations/channel-ui";
import { EmailLine } from "@/features/conversations/email-dialog";
import { MessageMediaGrid } from "@/features/conversations/message-media";
import { NoteItem } from "@/features/contact-timeline";

/** The contact's record behind the open thread, and the campaign's words for its outcomes: what
 *  editing a comment, and naming an outcome, need. Undefined until the record is loaded; `pending`
 *  while it has not answered yet. */
export const RecordContext = React.createContext<{
  contact: LeadCard | undefined;
  label: (slug: string) => string | undefined;
  pending: boolean;
}>({ contact: undefined, label: () => undefined, pending: false });

/** The open conversation's id, for the media route a picture is fetched from (ADR-140). Null on
 *  the development sample, which has no route to ask. A context rather than a prop because only
 *  the message bubble needs it, four levels down from the thread. */
export const ConversationIdContext = React.createContext<string | null>(null);

export function dayLabel(iso: string): string {
  const at = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today.getTime() - 86_400_000);
  if (at.toDateString() === today.toDateString()) return "Today";
  if (at.toDateString() === yesterday.toDateString()) return "Yesterday";
  return at.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
}

function timeOf(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

export function ActivityRows({
  activities, contactName, newId, live = false, label,
}: {
  activities: readonly Activity[];
  contactName: string;
  newId?: string;
  /** The activities are core-api's: an email's line opens it whole from `GET /messages/{id}`. */
  live?: boolean;
  /** What the list is, for a screen reader: the contact page's SMS tab names it. */
  label?: string;
}) {
  return (
    <ol className="space-y-2" aria-label={label}>
      {activities.map((a, i) => {
        const day = dayLabel(a.at);
        const newDay = i === 0 || dayLabel(activities[i - 1]!.at) !== day;
        return (
          <React.Fragment key={a.id}>
            {newDay && <Divider label={day} />}
            {a.id === newId && <Divider label="New" tone="accent" />}
            <li>
              <ActivityItem activity={a} contactName={contactName} live={live} />
            </li>
          </React.Fragment>
        );
      })}
    </ol>
  );
}

function Divider({ label, tone = "muted" }: { label: string; tone?: "muted" | "accent" }) {
  return (
    <li className="flex items-center gap-3 py-1">
      <span className={cn("h-px flex-1", tone === "accent" ? "bg-accent-7" : "bg-border")} />
      <span className={cn("text-2xs font-medium", tone === "accent" ? "text-accent-11" : "text-text-subtle")}>{label}</span>
      <span className={cn("h-px flex-1", tone === "accent" ? "bg-accent-7" : "bg-border")} />
    </li>
  );
}

function ActivityItem({ activity: a, contactName, live }: { activity: Activity; contactName: string; live: boolean }) {
  switch (a.kind) {
    case "message":
      return <MessageBubble message={a} contactName={contactName} live={live} />;
    case "comment":
      return <CommentNote comment={a} />;
    case "event":
      return <EventLine event={a} />;
    case "call":
      return (
        <div className={cn("flex", a.direction === "outbound" ? "justify-end" : "justify-start")}>
          <div className="flex w-full max-w-md flex-col gap-1">
            <CallActivityCard call={a} contactName={contactName} />
            <Meta at={a.at} align={a.direction === "outbound" ? "right" : "left"}>
              <TypeLabel type="call" />
            </Meta>
          </div>
        </div>
      );
  }
}

function TypeLabel({ type }: { type: "call" | MessageActivity["channel"] }) {
  const Icon = typeIcon(type);
  return (
    <span className={cn("inline-flex items-center gap-1", type === "call" ? "text-text-muted" : CHANNEL_TONES[type])}>
      <Icon className="size-3" aria-hidden />
      {type === "call" ? "Call" : CHANNEL_LABELS[type]}
    </span>
  );
}

/** The line under an activity: what it was, then the time. `detail` is what the time says under the
 *  pointer, for a line that leaves the rest out; without it the time names its full date. */
function Meta({
  at, align, detail, children,
}: {
  at: string;
  align: "left" | "right";
  detail?: string;
  children?: React.ReactNode;
}) {
  const time = (
    <time
      dateTime={at}
      title={detail ? undefined : formatDateTime(at)}
      tabIndex={detail ? 0 : undefined}
      className={cn(detail && "cursor-pointer")}
    >
      {timeOf(at)}
    </time>
  );
  return (
    <p className={cn("flex items-center gap-1.5 px-1 text-2xs text-text-subtle", align === "right" && "justify-end")}>
      {children}
      {children && <span aria-hidden>·</span>}
      {detail ? <Tooltip content={detail}>{time}</Tooltip> : time}
    </p>
  );
}

/**
 * A message, on the contact's side or ours. The contact's side carries their initials, ours the
 * initials of whoever on the team sent it, or a robot for a text nobody sent by hand. Under it is the
 * time alone (ACL-591): which number, who sent it and where it stands are under the pointer
 * (`messageDetail`), and the workflow or cadence is named there only to a supervisor and up. A text
 * that was not delivered says so beside the time, and why under it. One the system sent outside the
 * contact's hours is drawn as not yet gone, with when it goes out beside the time (ACL-630).
 *
 * An email is one line (`EmailLine`) that opens the whole of it, never its body in the thread: a
 * list carries only its first words, and its HTML is drawn nowhere but the dialog's sandboxed frame.
 * The line says who sent it and where it stands itself. One that arrived carries the contact's
 * initials only when core-api vouched it came from them.
 */
function MessageBubble({ message: m, contactName, live }: { message: MessageActivity; contactName: string; live: boolean }) {
  const conversationId = React.useContext(ConversationIdContext);
  const { contact } = React.useContext(RecordContext);
  const sees = seesAutomation(useAuth().user?.role);
  const out = m.direction === "outbound";
  const failed = m.status === "failed";
  const scheduled = out && m.status === "scheduled";
  const email = m.channel === "email";
  // The avatar is nudged up past the one-line Meta row under a text bubble; a message carrying a
  // picture is tall enough that the nudge only leaves it floating beside the image.
  const hasMedia = Boolean(m.media && m.media.length > 0);
  const nudge = cn("size-7 shrink-0", !hasMedia && "mb-5");
  return (
    <div className={cn("flex items-end gap-2", out ? "justify-end" : "justify-start")}>
      {!out && <Avatar name={email && !m.fromContact ? null : contactName} className={nudge} />}
      <div className={cn("flex max-w-[80%] flex-col gap-1 sm:max-w-md", out ? "items-end" : "items-start")}>
        {email ? (
          <EmailLine message={m} name={contactName} address={contact?.email} live={live} />
        ) : m.body ? (
          <p
            className={cn(
              "whitespace-pre-wrap break-words rounded-2xl px-3 py-2 text-sm",
              failed
                ? "rounded-br-sm border border-danger-6 bg-danger-2 text-danger-11"
                : scheduled
                  ? "rounded-br-sm border border-dashed border-accent-8 bg-accent-2 text-text"
                  : out
                  ? "rounded-br-sm bg-accent-9 text-white dark:text-gray-1"
                  : "rounded-bl-sm border border-border bg-surface text-text",
            )}
          >
            {m.body}
          </p>
        ) : null}
        {m.media && m.media.length > 0 && (
          <MessageMediaGrid
            conversationId={conversationId}
            media={m.media}
            align={out ? "right" : "left"}
          />
        )}
        {m.optOut && (
          <p className={cn("flex flex-wrap gap-1", out && "justify-end")}>
            <Tooltip content="They texted STOP: the contact is do-not-call">
              <Badge size="sm" tone="danger">STOP</Badge>
            </Tooltip>
          </p>
        )}
        <Meta at={m.at} align={out ? "right" : "left"} detail={messageDetail(m, sees)}>
          {email ? null : out && failed ? (
            <span className="inline-flex items-center gap-0.5 text-danger-11">
              <AlertCircle className="size-3" aria-hidden />
              {MESSAGE_STATUS_LABELS.failed}
            </span>
          ) : scheduled ? (
            <Tooltip content={SCHEDULED_WHY}>
              <span className="inline-flex items-center gap-0.5 text-accent-11" tabIndex={0}>
                <Clock className="size-3" aria-hidden />
                {scheduledFor(m.sendAt)}
              </span>
            </Tooltip>
          ) : null}
        </Meta>
        {out && failed && m.error && !email && (
          <p className="px-1 text-right text-2xs text-danger-11" role="note">
            {m.error}
          </p>
        )}
      </div>
      {out && (m.author ? <Avatar name={m.author.name} className={nudge} /> : <AutomatedAvatar className={nudge} />)}
    </div>
  );
}

/** Beside a text nobody on the team sent: no initials, since there is no person, and no workflow's. */
function AutomatedAvatar({ className }: { className?: string }) {
  return (
    <span
      className={cn("inline-flex items-center justify-center rounded-full border border-border bg-surface text-text-muted", className)}
      aria-hidden
    >
      <Bot className="size-3.5" />
    </span>
  );
}

function CommentNote({ comment }: { comment: CommentActivity }) {
  const Icon = CHANNEL_ICONS.comment;
  const { contact, label } = React.useContext(RecordContext);
  const sees = seesAutomation(useAuth().user?.role);
  // A workflow's note names the workflow only to a supervisor and up (ACL-591).
  const workflow = comment.note?.workflow;
  const author = workflow ? automatedBy(workflow, sees) : comment.author.name;
  const title = comment.note ? noteTitle(comment.note, label, sees) : comment.title;
  // A note on the record is shown as the record shows it, with Edit and Remove for whoever may.
  if (comment.note && contact) {
    return (
      <div className="flex justify-end">
        <div className="flex max-w-[80%] flex-col gap-1 sm:max-w-md">
          <div className="rounded-xl border border-warning-6 bg-warning-2 px-3 py-2">
            <p className="flex items-center gap-1.5 text-2xs font-medium text-warning-11">
              <StickyNote className="size-3" aria-hidden />
              Internal comment · only your team sees this
            </p>
            <div className="mt-1">
              <NoteItem note={comment.note} contact={contact} label={label} />
            </div>
          </div>
          <Meta at={comment.at} align="right">
            <span className="inline-flex items-center gap-1 text-warning-11">
              <Icon className="size-3" aria-hidden />
              {author}
            </span>
          </Meta>
        </div>
      </div>
    );
  }
  return (
    <div className="flex justify-end">
      <div className="flex max-w-[80%] flex-col gap-1 sm:max-w-md">
        <div className="rounded-xl border border-warning-6 bg-warning-2 px-3 py-2">
          <p className="flex items-center gap-1.5 text-2xs font-medium text-warning-11">
            <StickyNote className="size-3" aria-hidden />
            Internal comment · only your team sees this
          </p>
          {title && <p className="mt-1 text-xs font-medium text-text">{title}</p>}
          <p className="mt-1 whitespace-pre-wrap break-words text-sm text-text">{comment.body}</p>
        </div>
        <Meta at={comment.at} align="right">
          <span className="inline-flex items-center gap-1 text-warning-11">
            <Icon className="size-3" aria-hidden />
            {author}
          </span>
        </Meta>
      </div>
    </div>
  );
}

function EventLine({ event }: { event: EventActivity }) {
  const { contact } = React.useContext(RecordContext);
  const sees = seesAutomation(useAuth().user?.role);
  // A workflow's move names the workflow only to a supervisor and up (ACL-591).
  const text = event.workflow && sees ? `${event.text} · ${workflowSender(event.workflow)}` : event.text;
  const callback = event.callback;
  // A callback says when, on the lead's clock, and where it stands now — worked out at reading time,
  // so a promise that has come due says so.
  const when = callback
    ? callbackWhen(callback.due_at, knownZone(contact?.clock_timezone ?? contact?.timezone))
    : null;
  const status = callback ? callbackStatus(callback, Date.now()) : null;
  return (
    <div className="flex justify-center">
      <p className="inline-flex max-w-full flex-wrap items-center justify-center gap-x-2 gap-y-0.5 rounded-full border border-border bg-surface px-3 py-1 text-xs text-text">
        <CalendarClock className="size-3.5 shrink-0 text-text-muted" aria-hidden />
        <span className="truncate">{text}</span>
        {when && (
          <span className="text-text-muted" title={when.yours ?? undefined}>
            call back {when.there}
          </span>
        )}
        {status && (
          <Badge size="sm" tone={status.tone}>
            {status.label}
            {status.detail && ` · ${status.detail}`}
          </Badge>
        )}
        <time className="shrink-0 text-2xs text-text-subtle" dateTime={event.at} title={formatDateTime(event.at)}>
          {timeOf(event.at)}
        </time>
      </p>
    </div>
  );
}
